(function () {
  if (customElements.get('wholesale-order-form')) return;

  class WholesaleOrderForm extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.productsContainer = this.querySelector('[data-wholesale-products]');
      this.submitButton = this.querySelector('[data-wholesale-submit]');
      this.loadMoreLink = this.querySelector('[data-wholesale-load-more]');
      this.catalogStatus = this.querySelector('[data-wholesale-catalog-status]');
      this.orderStatus = this.querySelector('[data-wholesale-order-status]');
      this.lineCount = this.querySelector('[data-wholesale-line-count]');
      this.unitCount = this.querySelector('[data-wholesale-unit-count]');
      this.orderTotal = this.querySelector('[data-wholesale-total]');
      this.emptyState = this.querySelector('[data-wholesale-empty]');
      this.lightbox = this.querySelector('[data-wholesale-lightbox]');
      this.lightboxStage = this.querySelector('[data-lightbox-stage]');
      this.lightboxClose = this.querySelector('[data-lightbox-close]');
      this.confirmation = this.querySelector('[data-wholesale-confirmation]');
      this.confirmationOrderNumber = this.querySelector('[data-confirmation-order-number]');
      this.confirmationItems = this.querySelector('[data-confirmation-items]');
      this.confirmationTotal = this.querySelector('[data-confirmation-total]');
      this.confirmationClose = this.querySelector('[data-confirmation-close]');
      this.nextUrl = this.dataset.nextUrl || '';
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';

      this.boundHandleClick = this.handleClick.bind(this);
      this.boundHandleKeydown = this.handleKeydown.bind(this);
      this.boundHandleThumbHover = this.handleThumbHover.bind(this);
      this.addEventListener('click', this.boundHandleClick);
      // Warm the browser's cache for a thumbnail's full-size image on hover
      // or keyboard focus, so it's often already loaded by the time it's clicked.
      this.addEventListener('mouseover', this.boundHandleThumbHover);
      this.addEventListener('focusin', this.boundHandleThumbHover);
      document.addEventListener('keydown', this.boundHandleKeydown);

      // Locked (non-B2B) view has no product grid or submit button to enhance —
      // still wire up the lightbox container (harmless no-op if unused) and stop.
      if (!this.productsContainer || !this.submitButton) {
        this.setAttribute('data-enhanced', '');
        return;
      }

      this.addEventListener('input', this.handleInput.bind(this));
      this.addEventListener('change', this.handleChange.bind(this));
      this.submitButton.addEventListener('click', this.placeOrder.bind(this));

      if (this.loadMoreLink) {
        this.loadMoreLink.addEventListener('click', (event) => {
          event.preventDefault();
          this.loadRemainingProducts();
        });
      }

      this.setAttribute('data-enhanced', '');
      this.updateSummary();
      this.updateCatalogState();
      this.loadRemainingProducts();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.boundHandleKeydown);
      this.closeLightbox(false);
    }

    handleClick(event) {
      const imageTrigger = event.target.closest('[data-lightbox-trigger]');
      if (imageTrigger && this.contains(imageTrigger)) {
        this.openLightbox(imageTrigger);
        return;
      }

      const lightbox = event.target.closest('[data-wholesale-lightbox]');
      if (lightbox && this.contains(lightbox)) {
        this.closeLightbox();
        return;
      }

      const confirmationClose = event.target.closest('[data-confirmation-close]');
      if (confirmationClose && this.contains(confirmationClose)) {
        this.closeConfirmation();
        return;
      }

      const confirmationDone = event.target.closest('[data-confirmation-done]');
      if (confirmationDone && this.contains(confirmationDone)) {
        this.closeConfirmation();
        window.location.href = this.dataset.homeUrl || '/';
        return;
      }
      if (
        this.confirmation &&
        !this.confirmation.hidden &&
        event.target === this.confirmation
      ) {
        this.closeConfirmation();
        return;
      }

      const stepButton = event.target.closest('[data-quantity-step]');
      if (!stepButton || !this.contains(stepButton)) return;

      const row = stepButton.closest('[data-variant-row]');
      const input = row && row.querySelector('[data-quantity]');
      if (!input) return;

      const current = this.toInteger(input.value);
      const minimum = this.toInteger(row.dataset.minimum) || 1;
      const increment = this.toInteger(row.dataset.increment) || 1;
      const maximum = this.toOptionalInteger(row.dataset.maximum);
      let nextQuantity;

      if (stepButton.dataset.quantityStep === 'increase') {
        nextQuantity = current === 0 ? minimum : current + increment;
        if (maximum !== null && nextQuantity > maximum) {
          this.showQuantityLimit(row);
        } else {
          this.clearQuantityLimit(row);
        }
      } else {
        nextQuantity = current <= minimum ? 0 : current - increment;
        this.clearQuantityLimit(row);
      }

      input.value = this.normalizeQuantity(nextQuantity, minimum, increment, maximum);
      this.updateSummary();
    }

    handleInput(event) {
      if (event.target.matches('[data-quantity]')) {
        const input = event.target;
        const row = input.closest('[data-variant-row]');
        const minimum = this.toInteger(row.dataset.minimum) || 1;
        const increment = this.toInteger(row.dataset.increment) || 1;
        const maximum = this.toOptionalInteger(row.dataset.maximum);
        input.value = input.value.replace(/[^\d]/g, '');

        const requested = this.toInteger(input.value);
        if (maximum !== null && requested > maximum) {
          input.value = this.normalizeQuantity(maximum, minimum, increment, maximum);
          this.showQuantityLimit(row);
        } else if (maximum === null || requested < maximum) {
          this.clearQuantityLimit(row);
        }

        this.updateSummary();
      }
    }

    handleChange(event) {
      const input = event.target.closest('[data-quantity]');
      if (!input) return;

      const row = input.closest('[data-variant-row]');
      const minimum = this.toInteger(row.dataset.minimum) || 1;
      const increment = this.toInteger(row.dataset.increment) || 1;
      const maximum = this.toOptionalInteger(row.dataset.maximum);
      const requested = this.toInteger(input.value);

      if (maximum !== null && requested > maximum) {
        this.showQuantityLimit(row);
      } else if (maximum === null || requested < maximum) {
        this.clearQuantityLimit(row);
      }

      input.value = this.normalizeQuantity(requested, minimum, increment, maximum);
      this.updateSummary();
    }

    normalizeQuantity(quantity, minimum, increment, maximum) {
      if (quantity <= 0) return 0;

      let normalized = Math.max(quantity, minimum);
      normalized = minimum + Math.ceil((normalized - minimum) / increment) * increment;

      if (maximum !== null) {
        const highestValid =
          maximum < minimum
            ? 0
            : minimum + Math.floor((maximum - minimum) / increment) * increment;
        normalized = Math.min(normalized, highestValid);
      }

      return normalized;
    }

    showQuantityLimit(row) {
      const message = row.querySelector('[data-quantity-message]');
      if (!message || !row.dataset.limitMessage) return;

      message.textContent = row.dataset.limitMessage;
      message.hidden = false;
    }

    clearQuantityLimit(row) {
      const message = row.querySelector('[data-quantity-message]');
      if (!message) return;

      message.textContent = '';
      message.hidden = true;
    }

    updateCatalogState() {
      const products = this.querySelectorAll('[data-wholesale-product]');

      if (this.emptyState) {
        this.emptyState.hidden = products.length > 0 || Boolean(this.nextUrl);
      }
    }

    handleThumbHover(event) {
      const trigger = event.target.closest('[data-lightbox-trigger]');
      if (!trigger || !this.contains(trigger) || trigger.dataset.prefetched) return;
      trigger.dataset.prefetched = 'true';
      this.prefetchFullImage(trigger);
    }

    prefetchFullImage(trigger) {
      const src = trigger.dataset.imageSrc;
      if (!src) return null;
      const preload = new Image();
      preload.decoding = 'async';
      if (trigger.dataset.imageSrcset) {
        preload.sizes = '92vw';
        preload.srcset = trigger.dataset.imageSrcset;
      }
      preload.src = src;
      return preload;
    }

    openLightbox(trigger) {
      if (!this.lightbox || !this.lightboxStage) return;

      if (!this.lightboxImage) {
        this.lightboxImage = document.createElement('img');
        this.lightboxImage.className = 'wholesale-order-form__lightbox-image';
        this.lightboxImage.decoding = 'async';
        this.lightboxStage.appendChild(this.lightboxImage);
      }

      this.activeImageTrigger = trigger;
      const alt = trigger.dataset.imageAlt || '';
      const thumbImg = trigger.querySelector('img');

      // Show the already-loaded thumbnail immediately (scaled up, slightly
      // blurred via CSS) so there's no blank wait, then crossfade to the
      // full-resolution image once it's ready.
      this.lightboxImage.classList.remove('is-loaded');
      this.lightboxImage.removeAttribute('srcset');
      this.lightboxImage.alt = alt;
      this.lightboxImage.src = thumbImg ? thumbImg.currentSrc || thumbImg.src : '';

      this.lightbox.hidden = false;
      document.documentElement.classList.add('wholesale-lightbox-open');
      this.lightboxClose.focus();

      const preload = this.prefetchFullImage(trigger);
      trigger.dataset.prefetched = 'true';
      if (preload) {
        preload.onload = () => {
          if (this.activeImageTrigger !== trigger) return;
          if (trigger.dataset.imageSrcset) {
            this.lightboxImage.sizes = '92vw';
            this.lightboxImage.srcset = trigger.dataset.imageSrcset;
          }
          this.lightboxImage.src = trigger.dataset.imageSrc;
          this.lightboxImage.classList.add('is-loaded');
        };
      }
    }

    closeLightbox(restoreFocus = true) {
      if (!this.lightbox || this.lightbox.hidden) return;

      this.lightbox.hidden = true;
      this.lightboxImage.removeAttribute('srcset');
      this.lightboxImage.src = '';
      this.lightboxImage.alt = '';
      this.lightboxImage.classList.remove('is-loaded');
      document.documentElement.classList.remove('wholesale-lightbox-open');

      if (restoreFocus && this.activeImageTrigger) {
        this.activeImageTrigger.focus();
      }
      this.activeImageTrigger = null;
    }

    handleKeydown(event) {
      if (event.key !== 'Escape') return;
      if (this.lightbox && !this.lightbox.hidden) {
        this.closeLightbox();
      } else if (this.confirmation && !this.confirmation.hidden) {
        this.closeConfirmation();
      }
    }

    updateSummary() {
      let selectedLines = 0;
      let selectedUnits = 0;
      let total = 0;

      this.querySelectorAll('[data-variant-row]').forEach((row) => {
        const input = row.querySelector('[data-quantity]');
        const quantity = this.toInteger(input && input.value);
        row.toggleAttribute('data-selected', quantity > 0);

        if (quantity === 0) return;

        selectedLines += 1;
        selectedUnits += quantity;
        total += quantity * this.toInteger(row.dataset.price);
      });

      this.lineCount.textContent = selectedLines;
      this.unitCount.textContent = selectedUnits;
      this.orderTotal.textContent = this.formatMoney(total);
      this.submitButton.disabled = selectedLines === 0 || this.submitButton.dataset.loading === 'true';
    }

    async loadRemainingProducts() {
      if (!this.nextUrl || this.loadingCatalog) return;

      this.loadingCatalog = true;
      this.setAttribute('aria-busy', 'true');
      this.setCatalogStatus(this.dataset.loadingLabel);
      if (this.loadMoreLink) this.loadMoreLink.hidden = true;

      try {
        const loadedUrls = new Set();

        while (this.nextUrl) {
          const requestUrl = new URL(this.nextUrl, window.location.origin);
          requestUrl.searchParams.set('section_id', this.dataset.sectionId);
          const requestKey = requestUrl.toString();

          if (loadedUrls.has(requestKey)) {
            throw new Error('Shopify returned the same catalog page more than once.');
          }
          loadedUrls.add(requestKey);

          const response = await fetch(requestKey, {
            headers: { Accept: 'text/html' }
          });

          if (!response.ok) {
            throw new Error(`Catalog request failed with status ${response.status}.`);
          }

          const html = await response.text();
          const documentFragment = new DOMParser().parseFromString(html, 'text/html');
          const nextForm = documentFragment.querySelector('wholesale-order-form');
          const nextProducts = nextForm && nextForm.querySelector('[data-wholesale-products]');

          if (!nextForm || !nextProducts) {
            throw new Error('The next catalog page did not contain the wholesale order form.');
          }

          const existingVariantIds = new Set(
            Array.from(this.querySelectorAll('[data-variant-row]')).map(
              (row) => row.dataset.variantId
            )
          );

          Array.from(nextProducts.children).forEach((product) => {
            const hasNewVariant = Array.from(product.querySelectorAll('[data-variant-row]')).some(
              (row) => !existingVariantIds.has(row.dataset.variantId)
            );

            if (hasNewVariant) {
              this.productsContainer.appendChild(document.importNode(product, true));
            }
          });

          this.nextUrl = nextForm.dataset.nextUrl || '';
          this.updateCatalogState();
          this.updateSummary();
        }

        this.setCatalogStatus(this.dataset.loadedLabel);
      } catch (error) {
        console.error('Unable to load the complete wholesale catalog.', error);
        this.setCatalogStatus(this.dataset.loadErrorLabel, true);

        if (this.loadMoreLink && this.nextUrl) {
          this.loadMoreLink.href = this.nextUrl;
          this.loadMoreLink.hidden = false;
        }
      } finally {
        this.loadingCatalog = false;
        this.removeAttribute('aria-busy');
        this.updateCatalogState();
      }
    }

    collectSelectedItems() {
      const rows = Array.from(this.querySelectorAll('[data-variant-row]'));
      const items = [];

      rows.forEach((row) => {
        const input = row.querySelector('[data-quantity]');
        const minimum = this.toInteger(row.dataset.minimum) || 1;
        const increment = this.toInteger(row.dataset.increment) || 1;
        const maximum = this.toOptionalInteger(row.dataset.maximum);
        const requested = this.toInteger(input.value);
        const quantity = this.normalizeQuantity(
          requested,
          minimum,
          increment,
          maximum
        );

        if (maximum !== null && requested > maximum) {
          this.showQuantityLimit(row);
        }

        input.value = quantity;
        if (quantity > 0) {
          const product = row.closest('.wholesale-order-form__product');
          const productTitleEl = product && product.querySelector('.wholesale-order-form__product-title');
          const variantNameEl = row.querySelector('.wholesale-order-form__variant-name');
          const productTitle = productTitleEl ? productTitleEl.textContent.trim() : '';
          const variantName = variantNameEl ? variantNameEl.textContent.trim() : '';
          const title = variantName && variantName !== 'Standard' ? `${productTitle} \u2013 ${variantName}` : productTitle;

          items.push({
            id: row.dataset.variantId,
            quantity,
            title,
            price: this.toInteger(row.dataset.price)
          });
        }
      });


      this.updateSummary();
      return items;
    }

    // Creates a real Net 30 order via the backend (see create-wholesale-order.js) —
    // no Shopify cart, no checkout. Line item prices are never sent from here;
    // the backend re-derives the buyer's company catalog price server-side.
    async placeOrder() {
      const items = this.collectSelectedItems();
      if (items.length === 0) {
        this.resetSubmitButton();
        return;
      }

      if (!this.dataset.orderEndpoint) {
        this.setOrderStatus('Order endpoint is not configured for this page yet.', true);
        return;
      }

      this.submitButton.dataset.loading = 'true';
      this.submitButton.disabled = true;
      this.submitButton.textContent = this.dataset.placingLabel;
      this.setOrderStatus(this.dataset.placingOrderLabel);

      try {
        const response = await fetch(this.dataset.orderEndpoint, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            customerId: this.dataset.customerId || null,
            companyId: this.dataset.companyId || null,
            companyLocationId: this.dataset.companyLocationId || null,
            lineItems: items.map((item) => ({ variantId: item.id, quantity: item.quantity }))
          })
        });

        const result = await response.json().catch(() => ({}));

        if (!response.ok || !result.success) {
          throw new Error(result.error || this.dataset.orderErrorLabel);
        }

        const placedMessage = result.orderName
          ? `${this.dataset.orderPlacedLabel} (${result.orderName})`
          : this.dataset.orderPlacedLabel;
        this.setOrderStatus(placedMessage);
        this.showConfirmation(items, result.orderName);

        this.querySelectorAll('[data-quantity]').forEach((input) => {
          input.value = 0;
        });
        this.querySelectorAll('[data-variant-row]').forEach((row) => this.clearQuantityLimit(row));
        this.resetSubmitButton();
      } catch (error) {
        console.error('Unable to place the wholesale order.', error);
        this.setOrderStatus(error.message || this.dataset.orderErrorLabel, true);
        this.resetSubmitButton();
      }
    }

    resetSubmitButton() {
      this.submitButton.dataset.loading = 'false';
      this.submitButton.textContent = this.dataset.buttonLabel;
      this.updateSummary();
    }

    showConfirmation(items, orderName) {
      if (!this.confirmation) return;

      const total = items.reduce((sum, item) => sum + item.price * item.quantity, 0);

      if (this.confirmationOrderNumber) {
        this.confirmationOrderNumber.textContent = orderName ? `Order ${orderName}` : 'Order confirmed';
      }
      if (this.confirmationItems) {
        this.confirmationItems.innerHTML = items
          .map((item) => {
            const lineTotal = this.formatMoney(item.price * item.quantity);
            return `<li class="wholesale-order-form__confirmation-item">
              <span class="wholesale-order-form__confirmation-item-title">${item.quantity} \u00d7 ${this.escapeHtml(item.title)}</span>
              <span class="wholesale-order-form__confirmation-item-price">${lineTotal}</span>
            </li>`;
          })
          .join('');
      }
      if (this.confirmationTotal) {
        this.confirmationTotal.textContent = this.formatMoney(total);
      }

      this.confirmation.hidden = false;
      document.documentElement.classList.add('wholesale-lightbox-open');
      if (this.confirmationClose) this.confirmationClose.focus();
    }

    closeConfirmation() {
      if (!this.confirmation) return;
      this.confirmation.hidden = true;
      document.documentElement.classList.remove('wholesale-lightbox-open');
    }

    escapeHtml(value) {
      const div = document.createElement('div');
      div.textContent = value == null ? '' : String(value);
      return div.innerHTML;
    }

    setCatalogStatus(message, isError = false) {
      if (!this.catalogStatus) return;
      this.catalogStatus.textContent = message || '';
      this.catalogStatus.toggleAttribute('data-error', isError);
    }

    setOrderStatus(message, isError = false) {
      if (!this.orderStatus) return;
      this.orderStatus.textContent = message || '';
      this.orderStatus.toggleAttribute('data-error', isError);
    }

    formatMoney(cents) {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(cents, this.moneyFormat);
      }

      const placeholder = this.moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/);
      const token = placeholder ? placeholder[1] : 'amount';
      const options = {
        amount: { decimals: 2, thousands: ',', decimal: '.' },
        amount_no_decimals: { decimals: 0, thousands: ',', decimal: '.' },
        amount_with_comma_separator: { decimals: 2, thousands: '.', decimal: ',' },
        amount_no_decimals_with_comma_separator: {
          decimals: 0,
          thousands: '.',
          decimal: ','
        },
        amount_with_apostrophe_separator: { decimals: 2, thousands: "'", decimal: '.' }
      };
      const format = options[token] || options.amount;
      const value = (Number(cents || 0) / 100).toFixed(format.decimals);
      const parts = value.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, format.thousands);
      const formatted = parts.length === 2 ? parts.join(format.decimal) : parts[0];

      return placeholder ? this.moneyFormat.replace(placeholder[0], formatted) : formatted;
    }

    toInteger(value) {
      const parsed = Number.parseInt(value, 10);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
    }

    toOptionalInteger(value) {
      if (value === undefined || value === null || value === '') return null;
      return this.toInteger(value);
    }
  }

  customElements.define('wholesale-order-form', WholesaleOrderForm);
})();
